import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import useAuthStore from './stores/authStore';
import { canAccessRoute } from './lib/permissions';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PipelinePage from './pages/PipelinePage';
import ContactsPage from './pages/ContactsPage';
import ForecastsPage from './pages/ForecastsPage';
import TerritoriesPage from './pages/TerritoriesPage';
import ComingSoonPage from './pages/ComingSoonPage';
import UsersManagementPage from './pages/UsersManagementPage';
import AuditLogPage from './pages/AuditLogPage';
import AgendaPage from './pages/AgendaPage';
import AlliesInboxPage from './pages/AlliesInboxPage';
import PublicAllyRegisterPage from './pages/PublicAllyRegisterPage';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import './App.css';

const PAGE_CONFIG = {
  '/': { title: 'Dashboard', subtitle: 'Vista general del rendimiento' },
  '/pipeline': { title: 'Pipeline', subtitle: 'Gestión del embudo de ventas' },
  '/contactos': { title: 'Contactos', subtitle: 'Directorio de leads y clientes' },
  '/pronosticos': { title: 'Pronósticos', subtitle: 'Proyecciones de ventas' },
  '/territorios': { title: 'Territorios', subtitle: 'Gestión de zonas' },
  '/actividades': { title: 'Actividades', subtitle: 'Registro de interacciones' },
  '/cobranzas': { title: 'Cobranzas', subtitle: 'Gestión de cartera' },
  '/atencion': { title: 'Atención al Cliente', subtitle: 'Tickets de soporte' },
  '/reportes': { title: 'Reportes', subtitle: 'Análisis y métricas' },
  '/agenda': { title: 'Agenda', subtitle: 'Mis tareas y recordatorios' },
  '/casos-exito': { title: 'Casos de Éxito', subtitle: 'Material de entrenamiento' },
  '/reasignacion': { title: 'Reasignación de Cartera', subtitle: 'Balanceo de carga del equipo' },
  '/aprobaciones': { title: 'Aprobaciones', subtitle: 'Solicitudes pendientes de validación' },
  '/auditoria': { title: 'Auditoría', subtitle: 'Calificación de llamadas (QA)' },
  '/reportes-ejecutivos': { title: 'Reportes Ejecutivos', subtitle: 'ROI, LTV y configuración' },
  '/usuarios': { title: 'Gestión de Usuarios', subtitle: 'Administración de cuentas y roles (solo Gerente)' },
  '/aliados': { title: 'Aliados Comerciales', subtitle: 'Bandeja KYC y catálogo de descuentos (B2B)' },
  '/auditlog': { title: 'Historial de Acceso', subtitle: 'Sesiones, logouts y cambios de estado (zona Caracas)' },
};

function RequireRole({ children }) {
  const user = useAuthStore(s => s.user);
  const location = useLocation();
  if (!canAccessRoute(user, location.pathname)) {
    // Asesor visiting / (Dashboard) → redirect to Pipeline; everyone else → /
    const target = (location.pathname === '/' && user?.role === 'asesor') ? '/pipeline' : '/';
    return <Navigate to={target} replace />;
  }
  return children;
}

function ProtectedLayout() {
  const user = useAuthStore(s => s.user);
  const loading = useAuthStore(s => s.loading);
  const location = useLocation();

  if (loading) {
    return (
      <div className="app-loading">
        <img src="/assets/logo-k.png" alt="K" className="loading-logo" />
        <div className="loading-spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const config = PAGE_CONFIG[location.pathname] || PAGE_CONFIG['/'];

  return (
    <div data-testid="app-layout" className="app-layout">
      <Sidebar />
      <div className="main-area">
        <Topbar title={config.title} subtitle={config.subtitle} />
        <div className="content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function App() {
  const checkAuth = useAuthStore(s => s.checkAuth);
  const user = useAuthStore(s => s.user);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/aliados/registro" element={<PublicAllyRegisterPage />} />
        <Route element={<ProtectedLayout />}>
          {/* Core functional modules (MVP) */}
          {/* Dashboard is hidden for asesor — they land on /pipeline (their primary tool) */}
          <Route path="/" element={<RequireRole><DashboardPage /></RequireRole>} />
          <Route path="/pipeline" element={<RequireRole><PipelinePage /></RequireRole>} />
          <Route path="/contactos" element={<RequireRole><ContactsPage /></RequireRole>} />
          <Route path="/pronosticos" element={<RequireRole><ForecastsPage /></RequireRole>} />
          <Route path="/territorios" element={<RequireRole><TerritoriesPage /></RequireRole>} />
          <Route path="/usuarios" element={<RequireRole><UsersManagementPage /></RequireRole>} />
          <Route path="/aliados" element={<RequireRole><AlliesInboxPage /></RequireRole>} />
          <Route path="/auditlog" element={<RequireRole><AuditLogPage /></RequireRole>} />
          <Route path="/agenda" element={<RequireRole><AgendaPage /></RequireRole>} />
          <Route path="/casos-exito" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/reasignacion" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/aprobaciones" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/auditoria" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/reportes-ejecutivos" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/actividades" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/cobranzas" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/atencion" element={<RequireRole><ComingSoonPage /></RequireRole>} />
          <Route path="/reportes" element={<RequireRole><ComingSoonPage /></RequireRole>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
